import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import Seo, { breadcrumbSchema, localBusinessSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { ZONES, LOCALES } from "@/data/locales";

export default function Zones() {
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Zones d'intervention", to: "/zones-intervention/" },
  ];
  const byCountry = (c) => ZONES.filter((z) => z.country === c);
  const localesForZone = (key) => LOCALES.filter((l) => l.zoneKey === key);

  return (
    <>
      <Seo
        title="Zones d'intervention France & Suisse | Ronalp Enseigne"
        description="Nos zones d'intervention pour vos enseignes lumineuses et communication visuelle : Ain, Pays de Gex, Haute-Savoie, Lyon, Genève, Nyon, Lausanne et Vaud."
        path="/zones-intervention/"
        jsonLd={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 -z-10 bg-grid bg-grid-fade opacity-30" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <Breadcrumb items={crumbs} />
          <Reveal className="mt-8 max-w-3xl">
            <Overline>Zones d'intervention</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.98]">France &amp; Suisse</h1>
            <p className="mt-5 text-lg text-slate-300">Basés à Saint-Jean-de-Gonville, au cœur du Pays de Gex, nous intervenons sur un large territoire franco-suisse, avec une priorité sur le bassin lémanique et le Pays de Gex.</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 space-y-16">
        {["France", "Suisse"].map((country) => (
          <div key={country} data-testid={`zones-${country}`}>
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <span className="text-signature font-mono text-sm">{country === "Suisse" ? "CH" : "FR"}</span> {country}
              </h2>
            </Reveal>
            <Stagger className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {byCountry(country).map((z) => (
                <StaggerItem key={z.key}>
                  <div className="rounded-2xl border border-white/10 bg-ink-800 p-7 h-full">
                    <Link to={`/${z.hub}/`} className="group flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white group-hover:text-signature transition-colors flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-signature" /> {z.label}
                      </h3>
                      <ArrowUpRight className="h-5 w-5 text-chrome-mid group-hover:text-signature transition-colors" />
                    </Link>
                    <ul className="mt-5 flex flex-wrap gap-2">
                      {localesForZone(z.key).map((l) => (
                        <li key={l.slug}>
                          <Link to={`/${l.slug}/`} className="rounded-full border border-white/10 bg-ink-700 px-3.5 py-1.5 text-sm text-slate-300 hover:text-white hover:border-signature/40 transition-colors" data-testid={`zone-link-${l.slug}`}>
                            {l.nav}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        ))}
      </section>

      <CtaBand />
    </>
  );
}
